import React from 'react';
import { useTranslation } from 'react-i18next';
import { config } from 'src/config/config';
import { mapLocaleLang } from 'src/shared/helpers/mapLocaleLang';
import { ConsentTerms } from './ConsentTerms';
import { ConsentTermsKoreaTestId, ConsentTextKoreaTestId, RegistrationFormTestId } from './testIds';

export enum TermsTypeKorea {
    ACCEPT_ALL = 'ACCEPT_ALL',
    TOS_KOREA = 'TOS_KOREA',
    COLLECTION_AND_USE_OF_PERSONAL_INFO = 'COLLECTION_AND_USE_OF_PERSONAL_INFO',
    COLLECTION_AND_USE_OF_SENSITIVE_INFO = 'COLLECTION_AND_USE_OF_SENSITIVE_INFO',
    PROVISION_OF_PERSONAL_INFO_TO_THIRD_PARTIES = 'PROVISION_OF_PERSONAL_INFO_TO_THIRD_PARTIES',
    OVERSEAS_TRANSFER_OF_PERSONAL_INFO = 'OVERSEAS_TRANSFER_OF_PERSONAL_INFO',
    OPTIONAL_COLLECTION_AND_USE_OF_PERSONAL_INFO_FOR_MARKETING = 'OPTIONAL_COLLECTION_AND_USE_OF_PERSONAL_INFO_FOR_MARKETING',
}

interface Props {
    onChange: (value: string) => void;
    consentToAllAccepted: boolean;
    termsAccepted: boolean;
    privacyAccepted: boolean;
    collectAndUseSensitiveInfoAccepted: boolean;
    provisionOfPersonalInfoUseAccepted: boolean;
    overseasPersonalInfoXferAccepted: boolean;
    collectPersonalInfoForMarketingAccepted: boolean;
    url?: string | null;
}

export const ConsentTermsKorea: React.FC<Props> = props => {
    const {
        onChange,
        consentToAllAccepted,
        termsAccepted,
        privacyAccepted,
        collectAndUseSensitiveInfoAccepted,
        provisionOfPersonalInfoUseAccepted,
        overseasPersonalInfoXferAccepted,
        collectPersonalInfoForMarketingAccepted,
    } = props;
    const { i18n } = useTranslation();
    const { language } = i18n;
    const locale = {
        tou: mapLocaleLang(language),
        lang: language === 'ko' ? 'korean' : 'english',
    };
    const { tou, lang } = locale;

    return (
        <>
            <ConsentTerms
                type={TermsTypeKorea.ACCEPT_ALL}
                checkboxTestId={ConsentTermsKoreaTestId.AcceptAllKoreaTermsCheckBox}
                termsTestId={ConsentTextKoreaTestId.AcceptAllTestId}
                onChange={() => onChange('consentToAllAccepted')}
                isChecked={consentToAllAccepted}
            />
            <ConsentTerms
                type={TermsTypeKorea.TOS_KOREA}
                firstUrl={`${config.modernHealthURL}/terms-of-use${tou}`}
                secondUrl={`${config.modernHealthURL}/terms-of-use${tou}`}
                checkboxTestId={RegistrationFormTestId.TermsCheckBox}
                termsTestId={ConsentTextKoreaTestId.TosKoreaTestId}
                onChange={() => onChange('termsAccepted')}
                isChecked={termsAccepted}
            />
            <ConsentTerms
                type={TermsTypeKorea.COLLECTION_AND_USE_OF_PERSONAL_INFO}
                firstUrl={`${config.modernHealthURL}/korean-privacy-notice${
                    language === 'ko' ? '-ko' : ''
                }`}
                checkboxTestId={RegistrationFormTestId.PrivacyCheckBox}
                termsTestId={ConsentTextKoreaTestId.CollectionAndUseOfPersonalInfoTestId}
                onChange={() => onChange('privacyAccepted')}
                isChecked={privacyAccepted}
            />
            <ConsentTerms
                type={TermsTypeKorea.COLLECTION_AND_USE_OF_SENSITIVE_INFO}
                firstUrl={`${config.modernHealthURL}/${lang}-appendices#1`}
                checkboxTestId={ConsentTermsKoreaTestId.UseSensitiveInfoCheckBox}
                termsTestId={ConsentTextKoreaTestId.CollectionAndUseOfSensitiveInfoTestId}
                onChange={() => onChange('collectAndUseSensitiveInfoAccepted')}
                isChecked={collectAndUseSensitiveInfoAccepted}
            />
            <ConsentTerms
                type={TermsTypeKorea.PROVISION_OF_PERSONAL_INFO_TO_THIRD_PARTIES}
                firstUrl={`${config.modernHealthURL}/${lang}-appendices#2`}
                checkboxTestId={ConsentTermsKoreaTestId.ProvisionOfPersonalInfoUseAccepted}
                termsTestId={ConsentTextKoreaTestId.ProvisionOfPersonalInfoToThirdPartiesTestId}
                onChange={() => onChange('provisionOfPersonalInfoUseAccepted')}
                isChecked={provisionOfPersonalInfoUseAccepted}
            />
            <ConsentTerms
                type={TermsTypeKorea.OVERSEAS_TRANSFER_OF_PERSONAL_INFO}
                firstUrl={`${config.modernHealthURL}/${lang}-appendices#2`}
                checkboxTestId={ConsentTermsKoreaTestId.OverseasPersonalInfoXferAccepted}
                termsTestId={ConsentTextKoreaTestId.OverseasTransferOfPersonalInfoTestId}
                onChange={() => onChange('overseasPersonalInfoXferAccepted')}
                isChecked={overseasPersonalInfoXferAccepted}
            />
            <ConsentTerms
                type={TermsTypeKorea.OPTIONAL_COLLECTION_AND_USE_OF_PERSONAL_INFO_FOR_MARKETING}
                firstUrl={`${config.modernHealthURL}/${lang}-appendices#3`}
                checkboxTestId={ConsentTermsKoreaTestId.CollectPersonalInfoForMarketingAccepted}
                termsTestId={
                    ConsentTextKoreaTestId.OptionalCollectionAndUseOfPersonalInfoForMarketingTestId
                }
                onChange={() => onChange('collectPersonalInfoForMarketingAccepted')}
                isChecked={collectPersonalInfoForMarketingAccepted}
            />
        </>
    );
};
